/**
 * usePipelineCreationFlow — the "+ New pipeline" creation flow for the kanban
 * screen (SE-5487).
 *
 * Owns the New Pipeline modal's open/creating state and returns both the
 * `handleCreateBoard` trigger (wired to the toolbar's create button) and the
 * rendered `newPipelineModal` element (hook-returns-JSX, matching the screen's
 * `stageMovePromptComponent` convention). Extracted from MainContainer so that
 * container's already-large body does not grow past the max-lines-per-function
 * ratchet.
 *
 * Behavior:
 *   - On the PIPELINE surface, the create button opens the template picker
 *     modal; on Create it fires one `CreateKanbanBoard` mutation via the
 *     injected `createBoard` (the BE seeds the stages) and, for CUSTOM, opens
 *     the Stage Editor (via {@link useCreatePipeline}).
 *   - On the legacy WATCHLIST/ROSTER surfaces the create button keeps its
 *     prior auto-named behavior, so those flows are untouched.
 * @module features/player-kanban/hooks/usePipelineCreationFlow
 */

import { useCallback, useState, type ReactElement } from "react";

import NewPipelineModal from "../components/organisms/NewPipelineModal";

import { useCreatePipeline, type CreateBoardFn } from "./useCreatePipeline";

import { KanbanPipelineTemplate, KanbanSourceType } from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";

/** Arguments for {@link usePipelineCreationFlow}. */
export interface UsePipelineCreationFlowArgs {
  /** The active board source type (PIPELINE opens the template picker). */
  readonly sourceType: KanbanSourceType;
  /** Board creator from `useBoardManager` (accepts an optional template). */
  readonly createBoard: CreateBoardFn;
  /** Current board count — used to auto-name legacy non-pipeline boards. */
  readonly boardCount: number;
}

/** Result of {@link usePipelineCreationFlow}. */
export interface UsePipelineCreationFlowResult {
  /** Trigger for the toolbar's create button. */
  readonly handleCreateBoard: () => void;
  /** The rendered New Pipeline modal (render at the screen root). */
  readonly newPipelineModal: ReactElement;
}

/**
 * Drive the "+ New pipeline" creation flow.
 * @param args - {@link UsePipelineCreationFlowArgs}
 * @param args.sourceType - The active board source type (PIPELINE opens the picker).
 * @param args.createBoard - Board creator from `useBoardManager`.
 * @param args.boardCount - Current board count (auto-names legacy boards).
 * @returns The create trigger and the modal element.
 */
export function usePipelineCreationFlow({
  sourceType,
  createBoard,
  boardCount,
}: UsePipelineCreationFlowArgs): UsePipelineCreationFlowResult {
  const toast = useToastNotification();
  const createPipeline = useCreatePipeline(createBoard);
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const showError = useCallback(
    (error: unknown, fallback: string) =>
      toast.show({
        action: "danger",
        description: error instanceof Error ? error.message : fallback,
      }),
    [toast]
  );

  const handleCreateBoard = useCallback(async () => {
    // The PIPELINE surface creates from a template via the modal.
    if (sourceType === KanbanSourceType.Pipeline) {
      setIsOpen(true);
      return;
    }
    // Legacy WATCHLIST/ROSTER: keep the prior auto-named create behavior.
    try {
      await createBoard(`Pipeline ${boardCount + 1}`);
    } catch (error) {
      showError(error, "Failed to create board");
    }
  }, [sourceType, createBoard, boardCount, showError]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleCreate = useCallback(
    async (name: string, template: KanbanPipelineTemplate) => {
      setIsCreating(true);
      try {
        await createPipeline(name, template);
        setIsOpen(false);
      } catch (error) {
        showError(error, "Failed to create pipeline");
      } finally {
        setIsCreating(false);
      }
    },
    [createPipeline, showError]
  );

  // Key-reset on open so the picked template + typed name start fresh each time
  // the dialog opens (the sanctioned alternative to a set-state-in-effect reset).
  const newPipelineModal = (
    <NewPipelineModal
      key={isOpen ? "open" : "closed"}
      isOpen={isOpen}
      onClose={handleClose}
      onCreate={handleCreate}
      isCreating={isCreating}
    />
  );

  return { handleCreateBoard, newPipelineModal };
}
